import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { motion } from 'framer-motion';

const Button = ({
  children,
  href,
  variant = 'primary',
  size = 'medium',
  onClick,
  glow = false,
  ...props
}) => {
  const MotionComponent = href ? motion.a : motion.button;

  return (
    <StyledButton
      as={MotionComponent}
      href={href || undefined}
      target={href ? '_blank' : undefined}
      rel={href ? 'noopener noreferrer' : undefined}
      whileHover={{ scale: 1.03, y: -2 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 400, damping: 17 }}
      variant={variant}
      size={size}
      onClick={onClick}
      $glow={glow}
      {...props}
    >
      <ButtonContent>{children}</ButtonContent>
      {variant === 'primary' && <ShimmerOverlay />}
    </StyledButton>
  );
};

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const ButtonContent = styled.span`
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`;

const ShimmerOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: inherit;
  z-index: 1;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 60%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.08),
      transparent
    );
    animation: ${shimmer} 3s ease-in-out infinite;
  }
`;

const StyledButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: ${props => props.theme.fonts.body};
  text-decoration: none;
  overflow: hidden;
  border: none;

  ${props => {
    switch (props.variant) {
      case 'outline':
        return css`
          background: transparent;
          color: ${props.theme.colors.primary};
          border: 1.5px solid ${props.theme.colors.primary};
          
          &:hover {
            background: rgba(99, 102, 241, 0.08);
            box-shadow: 0 0 25px ${props.theme.colors.glow};
          }
        `;
      case 'ghost':
        return css`
          background: transparent;
          color: ${props.theme.colors.textSecondary};
          border: none;
          
          &:hover {
            color: ${props.theme.colors.text};
            background: rgba(255, 255, 255, 0.03);
          }
        `;
      default:
        return css`
          background: ${props.theme.colors.gradientPrimary};
          color: white;
          box-shadow: 0 4px 15px rgba(99, 102, 241, 0.3);
          
          &:hover {
            box-shadow: 0 8px 30px rgba(99, 102, 241, 0.5);
          }
        `;
    }
  }}

  ${props => {
    switch (props.size) {
      case 'small':
        return css`
          font-size: 0.8rem;
          padding: 8px 16px;
        `;
      case 'large':
        return css`
          font-size: 1rem;
          padding: 14px 32px;
        `;
      default:
        return css`
          font-size: 0.9rem;
          padding: 10px 24px;
        `;
    }
  }}

  ${props => props.$glow && css`
    &::before {
      content: '';
      position: absolute;
      inset: -2px;
      border-radius: inherit;
      background: ${props.theme.colors.gradientPrimary};
      z-index: -1;
      filter: blur(12px);
      opacity: 0.5;
    }
  `}
`;

export default Button;
